import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_EVENT, UPDATE_EVENT } from '../../../graphql/events/mutations'
import { EVENT_QUERY } from '../../../graphql/events/queries'
import type { Event } from '../../../graphql/events/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { focusFieldById, isRichTextTooLongError, MAX_RICH_TEXT_LENGTH } from '../../../lib/richText'
import { lowResPathFor } from '../../../lib/uploads'
import { Button, FormActions } from '../../atoms'
import {
  FormField,
  FormLoadState,
  ImageUploadField,
  RichTextField,
  TextareaField,
} from '../../molecules'

// <input type="datetime-local"> works in local time; toISOString() would shift by the UTC offset
function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

interface EventFormFieldsProps {
  event?: Event
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const EventFormFields = memo(
  ({ event, clientId, appId, onSuccess, onClose, skeleton }: EventFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!event
    const [title, setTitle] = useState(event?.title ?? '')
    const [description, setDescription] = useState(event?.description ?? '')
    const [dateStart, setDateStart] = useState(toDateTimeLocal(event?.date_start))
    const [dateEnd, setDateEnd] = useState(toDateTimeLocal(event?.date_end))
    const [label, setLabel] = useState(event?.label ?? '')
    const [place, setPlace] = useState(event?.place ?? '')
    const [image, setImage] = useState(event?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [observations, setObservations] = useState(event?.observations ?? '')
    const [errors, setErrors] = useState<{
      title?: string
      dateEnd?: string
      description?: string
    }>({})

    const makeOptimistic = useCallback(
      (id: string): Event => ({
        __typename: 'Event',
        id,
        client_id: clientId,
        application_id: appId,
        title,
        description: description || null,
        date_start: dateStart ? new Date(dateStart).toISOString() : null,
        date_end: dateEnd ? new Date(dateEnd).toISOString() : null,
        label: label || null,
        place: place || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        observations: observations || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, title, description, dateStart, dateEnd, label, place, image, observations],
    )

    const [createEvent, { loading: creating }] = useMutation(CREATE_EVENT, {
      optimisticResponse: { createEvent: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createEvent
        if (!item) {
          return
        }
        prependToLists(cache, 'events', item, { clientId, applicationId: appId })
      },
      onError(err) {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({ ...current, description: t('events.form.descriptionTooLong') }))
          focusFieldById('event-description')
        } else {
          showToast(err.message, 'error')
        }
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updateEvent, { loading: updating }] = useMutation(UPDATE_EVENT, {
      optimisticResponse: { updateEvent: makeOptimistic(event?.id ?? '') },
      onError(err) {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({ ...current, description: t('events.form.descriptionTooLong') }))
          focusFieldById('event-description')
        } else {
          showToast(err.message, 'error')
        }
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const validate = useCallback(() => {
      const errs: { title?: string; dateEnd?: string; description?: string } = {}
      if (!title.trim()) {
        errs.title = t('common.form.titleRequired')
      }
      // datetime-local values compare correctly as strings (same format, same zone)
      if (dateStart && dateEnd && dateEnd < dateStart) {
        errs.dateEnd = t('events.form.dateEndBeforeStart')
      }
      if (description.length > MAX_RICH_TEXT_LENGTH) {
        errs.description = t('events.form.descriptionTooLong')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [title, description, dateStart, dateEnd, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          title,
          description: description || null,
          date_start: dateStart ? new Date(dateStart).toISOString() : null,
          date_end: dateEnd ? new Date(dateEnd).toISOString() : null,
          label: label || null,
          place: place || null,
          image: image || null,
          observations: observations || null,
        }
        if (isEdit) {
          updateEvent({ variables: { id: event.id, ...vars } })
        } else {
          createEvent({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        event?.id,
        title,
        description,
        dateStart,
        dateEnd,
        label,
        place,
        image,
        observations,
        clientId,
        appId,
        updateEvent,
        createEvent,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="event-title"
          label={t('events.form.titleLabel')}
          name="title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          error={errors.title}
          required
        />
        <FormField
          skeleton={skeleton}
          id="event-place"
          label={t('events.form.placeLabel')}
          name="place"
          value={place}
          onChange={e => setPlace(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="event-label"
          label={t('events.form.labelField')}
          name="label"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="event-date-start"
          label={t('events.form.dateStartLabel')}
          name="date_start"
          type="datetime-local"
          value={dateStart}
          onChange={e => setDateStart(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="event-date-end"
          label={t('events.form.dateEndLabel')}
          name="date_end"
          type="datetime-local"
          value={dateEnd}
          onChange={e => setDateEnd(e.target.value)}
          min={dateStart || undefined}
          error={errors.dateEnd}
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="event-image-file"
          target="events"
          label={t('events.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <RichTextField
          skeleton={skeleton}
          id="event-description"
          label={t('events.form.descriptionLabel')}
          value={description}
          onChange={setDescription}
          error={errors.description}
        />
        <TextareaField
          skeleton={skeleton}
          id="event-observations"
          label={t('events.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('events.form.saving') : t('events.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

EventFormFields.displayName = 'EventFormFields'

interface EventFormProps extends Omit<EventFormFieldsProps, 'event'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const EventForm = memo(({ id, ...props }: EventFormProps) => {
  const { record, status } = useRecordById(EVENT_QUERY, id, data => data.event)
  if (status === 'loading') {
    return <EventFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <EventFormFields key={id ?? 'new'} event={record} {...props} />
})

EventForm.displayName = 'EventForm'
